import './style.css';

let onboardingToken = new URLSearchParams(window.location.search).get('token');
let earlyInstallPrompt;
let installing = false;
let latestUser;
let showInstallUI;
let dismissPWAInstallation;
let showInstallReminder;
let showProfileCreatedSuccess;
let awaitingProfileCreatedChoice = false;
let resolvingOnboardingLink = false;
let onboardingProfileCreatorUserId;

// The browser fires beforeinstallprompt once, so capture it before the app
// decides whether to offer installation.
window.addEventListener('beforeinstallprompt', (event) => {
  earlyInstallPrompt = event;
});

const main = async () => {
  const root = document.getElementById('root');
  const loaderText = root.querySelector('.loader-text');
  let router;

  const setLoaderText = (text) => {
    if (loaderText) {
      loaderText.textContent = text;
    }
  };

  setLoaderText('Initializing application...');

  const { default: Router } = await import('awcf/Router');
  const { setPage } = await import('./firebase/analytics/index.js');
  const { onAuthChanged } = await import('./firebase/auth/index.js');
  const {
    getNetworkById,
    getNetworks,
    getReservationById,
    resolveOnboardingLink,
  } = await import('./models/networks.js');
  const { registerServiceWorker } = await import('./register-sw.js');

  setLoaderText('Registering service worker...');

  const swRegistration = await registerServiceWorker({
    onUpdateAvailable: async (worker) => {
      const { default: UpdateBanner } = await import('./components/UpdateBanner');
      document.body.appendChild(new UpdateBanner(worker));
    },
  });

  setLoaderText('Checking authentication...');

  const handleUser = async (user) => {
    latestUser = user;

    if (!user) {
      onboardingProfileCreatorUserId = null;
      import('./components/SignIn').then(({ default: SignIn }) => {
        router = null;
        root.innerHTML = '';
        root.appendChild(new SignIn());
        setPage('Sign In');
      });
      return;
    }

    if (!user.activeProfile) {
      if (onboardingProfileCreatorUserId === user.uid) {
        return;
      }

      showProfileCreatedSuccess = () => import('./components/ProfileCreatedSuccess')
        .then(({ default: ProfileCreatedSuccess }) => {
          awaitingProfileCreatedChoice = true;
          router = null;
          root.innerHTML = '';
          root.appendChild(new ProfileCreatedSuccess(
            showInstallUI ? () => {
              if (showInstallUI) {
                installing = true;
                showInstallUI();
              }
            } : null,
            () => {
              if (showInstallUI) {
                dismissPWAInstallation();
              }
              showInstallUI = null;
              showProfileCreatedSuccess = null;
              awaitingProfileCreatedChoice = false;
              if (latestUser?.activeProfile) {
                handleUser(latestUser);
              }
            },
          ));
          setPage('Profile Created');
        });

      const showProfileCreator = (network, reservation, token) => import('./components/ProfileCreator')
        .then(({ default: ProfileCreator }) => {
          router = null;
          root.innerHTML = '';
          root.appendChild(new ProfileCreator({
            user,
            network,
            reservation,
            onboardingToken: token,
          }, null, showProfileCreatedSuccess));
          setPage('Create Profile');
        });

      const showReservationValidator = () => import('./components/ReservationValidator')
        .then(({ default: ReservationValidator }) => {
          router = null;
          root.innerHTML = '';
          const reservationValidator = new ReservationValidator();
          root.appendChild(reservationValidator);
          setPage('Validate Reservation');
          reservationValidator.addEventListener('valid', (event) => {
            const { network, reservation } = event.detail;
            showProfileCreator(network, reservation);
          });
        });

      if (resolvingOnboardingLink) {
        return;
      }

      const token = onboardingToken;
      onboardingToken = null;

      if (!token) {
        showReservationValidator();
        return;
      }

      resolvingOnboardingLink = true;
      router = null;
      root.innerHTML = `
        <div class="loader">
          <div class="loader-animation">
            <img width="80" src="/logo.svg" alt="innhapp.com logo" />
          </div>
          <div class="loader-text">Validating reservation...</div>
        </div>
      `;

      try {
        const { networkId, reservationId } = await resolveOnboardingLink(token);
        const [networks, reservation] = await Promise.all([
          getNetworks(),
          getReservationById(networkId, reservationId),
        ]);
        const network = networks.find(({ id }) => id === networkId);
        const fiveDaysFromNow = new Date(Date.now() + 5 * 24 * 60 * 60 * 1000);

        if (!network || reservation.checkInDate > fiveDaysFromNow) {
          throw new Error('Onboarding link cannot be used yet');
        }

        await showProfileCreator(network, reservation, token);
        onboardingProfileCreatorUserId = user.uid;
      } catch (error) {
        console.error('Error resolving onboarding link:', error);
        showReservationValidator();
      } finally {
        resolvingOnboardingLink = false;
      }
      return;
    }

    onboardingProfileCreatorUserId = null;

    if (installing) {
      return;
    }

    if (awaitingProfileCreatedChoice) {
      return;
    }

    const { activeProfile } = user;
    const network = await getNetworkById(activeProfile.id);

    if (installing) {
      return;
    }

    if (!network) {
      alert('Your active profile is linked to a network that no longer exists.');
      return;
    }

    const state = {
      user,
      activeProfile,
      network,
    };

    const colorScheme = user?.doc?.settings?.colorScheme;

    if (colorScheme) {
      document.documentElement.setAttribute('data-color-scheme', colorScheme);
    }

    if (!router) {
      const routes = await import('./routes').then(m => m.default);
      const AppBar = await import('./components/AppBar').then(m => m.default);
      const BottomNavigation = await import('./components/BottomNavigation').then(m => m.default);
      const CheckoutFeedback = await import('./components/CheckoutFeedback').then(m => m.default);

      if (installing) {
        return;
      }

      const appBar = new AppBar(state);
      const bottomNavigation = new BottomNavigation(state);

      Object.assign(state, { appBar, bottomNavigation });

      router = new Router(routes, state, import.meta.env.BASE_URL);
      router.prepend(appBar);
      router.appendChild(bottomNavigation);
      router.appendChild(new CheckoutFeedback(state));

      root.innerHTML = '';
      root.appendChild(router);
    } else {
      router.setState(state);
    }

    showInstallReminder = async () => {
      if (!showInstallUI || document.querySelector('x-install-reminder')) {
        return;
      }

      const { default: InstallReminder } = await import('./components/InstallReminder');
      if (document.querySelector('x-install-reminder')) {
        return;
      }

      document.body.appendChild(new InstallReminder(
        () => {
          installing = true;
          showInstallUI();
        },
        () => {
          dismissPWAInstallation();
          showInstallUI = null;
        },
      ));
    };
    showInstallReminder();
  };

  onAuthChanged(swRegistration, handleUser);
};

window.addEventListener('load', async () => {
  root.querySelector('.loader-text').textContent = 'Loading application...';
  const install = await import('./install.js');
  dismissPWAInstallation = install.dismissPWAInstallation;
  install.promptPWAInstallation(main, earlyInstallPrompt, (showInstall) => {
    showInstallUI = showInstall;
    if (sessionStorage.getItem('pwa-install-requested')) {
      sessionStorage.removeItem('pwa-install-requested');
      installing = true;
      showInstallUI();
      return;
    }
    if (awaitingProfileCreatedChoice && showProfileCreatedSuccess) {
      showProfileCreatedSuccess();
      return;
    }
    if (showInstallReminder) {
      showInstallReminder();
    }
  });
});

// window.matchMedia('(display-mode: standalone)').addEventListener('change', () => {
//   // Log display mode change to analytics
//   console.log('DISPLAY_MODE_CHANGED', getPWADisplayMode());
// });
